import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import {
    assetTypeLabel,
    cannotCommission,
    describeProperties,
    draftChanges,
    draftOf,
    freelancersOf,
    PRIORITIES,
    priorityMeta,
    SPEC_ASSET_TYPES,
    specPropertiesFor,
    statusMeta,
} from '../../lib/art-pipeline.js';
import { BaseComponent } from '../../lib/base-component.js';
import '../shared/image-lightbox.js';
import '../shared/media-thumb.js';
import '../shared/upload-progress.js';

/**
 * A side panel over one spec (issue #195): a form for its name, type and the
 * type's properties, priority, milestone and brief, saved only with Save; its
 * reference images; its commissions, and "+ Commission" to make another; and
 * archive or unarchive (issue #227): specs are archived, never deleted, and
 * archiving leaves the spec's commissions as they are.
 *
 * Fires `spec-changed` (detail: the updated spec), `commission` (detail: its
 * id, to open the commission form) and `close`.
 */
class PipelineSpecPanel extends BaseComponent {
    static properties = {
        spec: { type: Object, attribute: false },
        milestones: { type: Array, attribute: false },
        userLabels: { type: Object, attribute: false },
        _draft: { type: Object, state: true },
        _closeBlocked: { type: Boolean, state: true },
        _busy: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _lightboxSrc: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.25); z-index: 9997; }
        .panel {
            position: fixed; top: 0; right: 0; bottom: 0; width: min(28rem, 100vw);
            background: white; z-index: 9998; box-shadow: -0.5rem 0 2rem rgba(0,0,0,0.15);
            display: flex; flex-direction: column;
        }
        .panel-header { display: flex; justify-content: space-between; align-items: start; padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6; gap: 1rem; }
        .panel-header h5 { margin: 0; font-weight: 600; word-break: break-word; }
        .panel-body { padding: 1rem 1.25rem; overflow-y: auto; flex: 1; font-size: 0.9rem; }
        h6 { font-weight: 600; font-size: 0.85rem; margin: 1.25rem 0 0.5rem; display: flex; justify-content: space-between; align-items: center; }
        .meta { display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center; color: #555; font-size: 0.85rem; }
        .ref-images-grid { display: flex; flex-wrap: wrap; gap: 0.5rem; }
        .ref-thumb { width: 6rem; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
        .ref-thumb-info { display: flex; justify-content: space-between; padding: 0.2rem 0.4rem; font-size: 0.7rem; color: #666; }
        .commissions { list-style: none; padding: 0; margin: 0; border: 1px solid #e5e7eb; border-radius: 6px; }
        .commissions li { padding: 0.5rem 0.75rem; border-bottom: 1px solid #f0f0f0; }
        .commissions li:last-child { border-bottom: 0; }
        .commissions .who { font-size: 0.8rem; color: #666; }
        .panel-footer { padding: 0.75rem 1.25rem; border-top: 1px solid #dee2e6; display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
        .fields { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem 0.75rem; }
        .fields .wide { grid-column: 1 / -1; }
        .fields label { font-size: 0.75rem; color: #666; margin-bottom: 0.15rem; }
        .follows { font-size: 0.85rem; padding: 0.25rem 0; }
        .unsaved { font-size: 0.8rem; color: #b45309; }
    `;

    constructor() {
        super();
        this.spec = null;
        this.milestones = [];
        this.userLabels = new Map();
        this._base = null;
        this._draft = null;
        this._closeBlocked = false;
        this._busy = false;
        this._error = null;
        this._lightboxSrc = null;
        this._specId = null;
    }

    willUpdate(changed) {
        if (!changed.has('spec') || !this.spec) return;
        if (this.spec.id !== this._specId) {
            // Another spec: start its draft afresh.
            this._specId = this.spec.id;
            this._resetDraft();
            this._error = null;
        } else if (!this._dirty) {
            // The same spec changed elsewhere (an image, a commission): follow
            // it unless there are edits in hand.
            this._resetDraft();
        }
    }

    _resetDraft() {
        this._base = draftOf(this.spec);
        this._draft = draftOf(this.spec);
        this._closeBlocked = false;
    }

    get _changes() {
        return this._draft
            ? draftChanges(this._base, this._draft, this.spec.commissions.length > 0)
            : {};
    }

    get _dirty() {
        return Object.keys(this._changes).length > 0;
    }

    _set(field, value) {
        this._draft = { ...this._draft, [field]: value };
        this._closeBlocked = false;
    }

    _close() {
        if (this._dirty) {
            this._closeBlocked = true;
            return;
        }
        this.dispatchEvent(new CustomEvent('close'));
    }

    _setType(type) {
        // A type has its own properties, so a new type starts without any.
        this._draft = { ...this._draft, asset_type: type, properties: {} };
        this._closeBlocked = false;
    }

    _setProperty(key, value) {
        this._set('properties', { ...this._draft.properties, [key]: value });
    }

    _discard() {
        this._resetDraft();
        this._error = null;
    }

    _save() {
        const changes = this._changes;
        if (!Object.keys(changes).length) return;
        if (changes.name === '') {
            this._error = 'Name can’t be empty';
            return;
        }
        return this._run(async () => {
            const updated = await api.patch(`/api/specs/${this.spec.id}`, changes);
            this._base = draftOf(updated);
            this._draft = draftOf(updated);
            this._closeBlocked = false;
            this._changed(updated);
        });
    }

    _changed(updated) {
        this.dispatchEvent(new CustomEvent('spec-changed', { detail: updated }));
    }

    async _run(fn) {
        this._busy = true;
        this._error = null;
        try {
            await fn();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._busy = false;
        }
    }

    async _reload() {
        this._changed(await api.get(`/api/specs/${this.spec.id}`));
    }

    _onUploadComplete(e) {
        return this._run(async () => {
            const form = new FormData();
            form.append('upload_id', e.detail.upload_id);
            await api.upload(`/api/specs/${this.spec.id}/reference-images`, form);
            await this._reload();
            const uploader = /** @type {any} */ (this.shadowRoot.querySelector('upload-progress'));
            if (uploader) uploader.reset();
        });
    }

    _removeImage(imageId) {
        return this._run(async () => {
            await api.delete(`/api/specs/${this.spec.id}/reference-images/${imageId}`);
            await this._reload();
        });
    }

    _archive() {
        return this._run(async () => {
            const data = await api.post('/api/specs/archive', { ids: [this.spec.id] });
            this._changed(data.specs[0]);
        });
    }

    _unarchive() {
        return this._run(async () => {
            const data = await api.post('/api/specs/unarchive', { ids: [this.spec.id] });
            this._changed(data.specs[0]);
        });
    }

    _commission() {
        this.dispatchEvent(new CustomEvent('commission', { detail: this.spec.id }));
    }

    render() {
        const spec = this.spec;
        if (!spec) return nothing;
        const status = statusMeta(spec.status);
        const priority = priorityMeta(spec.priority);
        const props = describeProperties(spec);
        return html`
            <div class="backdrop" @click="${this._close}"></div>
            <aside class="panel" role="dialog" aria-label="${spec.name}"
                @keydown="${(e) => e.key === 'Escape' && this._close()}">
                <div class="panel-header">
                    <div>
                        <h5>${spec.name}</h5>
                        <div class="meta mt-1">
                            <span>${assetTypeLabel(spec.asset_type)}${props ? ` · ${props}` : ''}</span>
                            <span class="badge ${priority.badge}">${priority.label}</span>
                            <span class="badge ${status.badge}">${status.label}</span>
                            ${spec.milestone_name ? html`<span><i class="bi bi-flag"></i> ${spec.milestone_name}</span>` : nothing}
                        </div>
                    </div>
                    <button class="btn-close" aria-label="Close" @click="${this._close}"></button>
                </div>
                <div class="panel-body">
                    ${this._error ? html`<div class="alert alert-danger py-2 small">${this._error}</div>` : nothing}

                    ${this._renderFields(spec)}

                    <h6>Brief</h6>
                    <textarea class="form-control form-control-sm" rows="6" .value="${this._draft.brief}"
                        @input="${(e) => this._set('brief', e.target.value)}"
                        placeholder="What it's for, style, pose, where it's used…"></textarea>
                    ${spec.commissions.length ? html`<div class="form-text">Every commission of this spec shows its brief.</div>` : nothing}

                    <h6>Reference images</h6>
                    <div class="form-text mt-0 mb-2">Images save as soon as they upload, and go as soon as you remove them.</div>
                    ${
                        spec.reference_images.length
                            ? html`<div class="ref-images-grid mb-2">
                            ${spec.reference_images.map(
                                (img) => html`<div class="ref-thumb">
                                    <media-thumb src="${img.file_url}" alt="${img.file_name}" type="${img.mime_type || ''}"
                                        @media-click="${() => (this._lightboxSrc = img.file_url)}"></media-thumb>
                                    <div class="ref-thumb-info">
                                        <span class="text-truncate">${img.file_name}</span>
                                        <button class="btn btn-link btn-sm p-0 text-danger" style="font-size: 0.7rem; line-height: 1;" ?disabled="${this._busy}"
                                            @click="${() => this._removeImage(img.id)}" aria-label="Remove image"><i class="bi bi-trash"></i></button>
                                    </div>
                                </div>`,
                            )}
                        </div>`
                            : nothing
                    }
                    <upload-progress .api="${api}" accept="image/*,video/webm" label="Drop image or click to browse"
                        @upload-complete="${this._onUploadComplete}"></upload-progress>

                    ${this._renderCommissions(spec)}
                </div>
                <div class="panel-footer">
                    ${this._dirty ? this._renderSaveBar() : this._renderActions(spec)}
                </div>
            </aside>
            ${this._lightboxSrc ? html`<image-lightbox src="${this._lightboxSrc}" @close="${() => (this._lightboxSrc = null)}"></image-lightbox>` : nothing}
        `;
    }

    _renderSaveBar() {
        return html`
            <span class="unsaved">
                <i class="bi bi-exclamation-circle"></i>
                ${this._closeBlocked ? 'Save or discard your changes to close.' : 'Unsaved changes'}
            </span>
            <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-secondary" ?disabled="${this._busy}" @click="${this._discard}">Discard</button>
                <button class="btn btn-sm btn-primary" ?disabled="${this._busy}" @click="${this._save}">Save changes</button>
            </div>
        `;
    }

    _renderActions(spec) {
        return html`
            ${
                spec.archived_at
                    ? html`<button class="btn btn-sm btn-outline-secondary" ?disabled="${this._busy}" @click="${this._unarchive}"
                    title="Bring it back into the pipeline"><i class="bi bi-arrow-counterclockwise"></i> Unarchive spec</button>`
                    : html`<button class="btn btn-sm btn-outline-secondary" ?disabled="${this._busy}" @click="${this._archive}"
                    title="Take it out of the pipeline; its commissions stay as they are"><i class="bi bi-archive"></i> Archive spec</button>`
            }
            <button class="btn btn-sm btn-outline-secondary" @click="${this._close}">Close</button>
        `;
    }

    _renderFields(spec) {
        const d = this._draft;
        const commissioned = spec.commissions.length > 0;
        const select = (value, options, onChange, { disabled = false, title = '' } = {}) => html`
            <select class="form-select form-select-sm" ?disabled="${disabled}" title="${title}"
                @change="${(e) => onChange(e.target.value)}">
                ${options.map((o) => html`<option value="${o.value}" ?selected="${o.value === value}">${o.label}</option>`)}
            </select>`;
        return html`
            <div class="fields">
                <div class="wide">
                    <label for="name">Name</label>
                    <input id="name" class="form-control form-control-sm" .value="${d.name}"
                        @input="${(e) => this._set('name', e.target.value)}">
                </div>
                <div>
                    <label>Type</label>
                    ${select(d.asset_type, SPEC_ASSET_TYPES, (v) => this._setType(v), {
                        disabled: commissioned,
                        title: commissioned ? 'Locked: this spec has a commission' : '',
                    })}
                </div>
                <div><label>Priority</label>${select(d.priority, PRIORITIES, (v) => this._set('priority', v))}</div>
                ${specPropertiesFor(d.asset_type).map(
                    (p) => html`<div>
                        <label>${p.label}</label>
                        ${select(
                            d.properties[p.key] || '',
                            [
                                {
                                    value: '',
                                    label: p.neededToCommission ? 'Not decided yet' : 'None',
                                },
                                ...p.options,
                            ],
                            (v) => this._setProperty(p.key, v),
                        )}
                    </div>`,
                )}
                <div class="wide">
                    <label>Milestone</label>
                    ${select(
                        d.milestone_id,
                        [
                            { value: '', label: 'No milestone' },
                            ...this.milestones.map((m) => ({ value: m.id, label: m.name })),
                        ],
                        (v) => this._set('milestone_id', v),
                    )}
                </div>
            </div>
        `;
    }

    _renderCommissions(spec) {
        const blocked = cannotCommission(spec);
        const people = new Map(freelancersOf(spec, this.userLabels).map((f) => [f.id, f.label]));
        return html`
            <h6>
                <span>Commissions</span>
                <button class="btn btn-sm btn-primary py-0" ?disabled="${this._dirty || Boolean(blocked)}"
                    title="${this._dirty ? 'Save or discard your changes first' : blocked || 'Make a commission on this spec'}"
                    @click="${this._commission}"><i class="bi bi-plus-lg"></i> Commission</button>
            </h6>
            ${
                spec.commissions.length
                    ? html`<ul class="commissions">
                    ${spec.commissions.map((c) => {
                        const who = c.assigned_to
                            ? `Assigned to ${people.get(c.assigned_to) || c.assigned_to_username || 'a freelancer'}`
                            : c.offered_to
                              ? `Offered to ${people.get(c.offered_to) || c.offered_to_username || 'a freelancer'}`
                              : 'Not yet offered to a freelancer';
                        return html`<li>
                            <div><a href="/commissions/${c.id}">${c.slug}</a>
                                <span class="badge bg-light text-dark border">${c.status}</span>
                                ${c.rate_cents != null ? html`<span class="small text-muted">$${(c.rate_cents / 100).toFixed(2)}</span>` : nothing}</div>
                            <div class="who">${who}</div>
                        </li>`;
                    })}
                </ul>`
                    : html`<div class="small text-muted">${blocked || 'Not commissioned yet.'}</div>`
            }
        `;
    }
}

customElements.define('pipeline-spec-panel', PipelineSpecPanel);
